import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from 'antd';
import { AuthProvider } from './context/AuthContext';
import { useAuth } from './context/AuthContext';
import { useTheme } from './context/ThemeContext';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import AccountsPage from './pages/AccountsPage';
import DatabricksAccountsPage from './pages/DatabricksAccountsPage';
import PipelineDiscoveryPage from './pages/PipelineDiscoveryPage';
import ApplicationsPage from './pages/ApplicationsPage';
import ApplicationDetailPage from './pages/ApplicationDetailPage';
import WorkflowsPage from './pages/WorkflowsPage';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import MetricsPage from './pages/MetricsPage'; 
import LogsPage from './pages/LogsPage';
import AlertsPage from './pages/AlertsPage';
import IncidentsPage from './pages/IncidentsPage';
import ConnectorsPage from './pages/ConnectorsPage';
import CostMonitor from './pages/CostMonitor';
import AboutPage from './pages/AboutPage';
import LLMObservability from './pages/LLMObservabilityPage';
import AIDebugger from './pages/AIDebuggerpage';
import CallbackPage from './components/CallbackPage';
//import OverviewPage from './pages/OverviewPage';
import ActiveServicesTracesPage from "./pages/ActiveServicesTracesPage";
import DatabricksMetricsOnboardingPage from './pages/DatabricksMetricsOnboardingPage';
import ContactPage from './pages/ContactPage';
import AllGraphsTest from './pages/AllGraphsTest';
import './components/Layout/global.css'; // Import global styles
import { useTokenRefresher } from './components/TokenRefresher';
import { isAuthEnabled } from './components/authConfig';
import { AIProvider } from './context/AIContext';
import BackgroundRunner from './components/BackgroundRunner';
import DashboardList from './pages/DashboardList';
import { savePostLoginRedirect } from './utils/postLoginRedirect';

const ApplicationDiscoveryRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/applications${location.search || ''}`} replace />;
};

const ApiDocsDrawerLazy = lazy(() => import('./components/ApiDocsDrawer/ApiDocsDrawer'));
const { Content } = Layout;

const ProtectedRouteWithRefresh = ({ children }) => {
  useTokenRefresher();
  return children;
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  if (!user) {
    // Keep Teams Silence / View Alert deep links across SSO.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    savePostLoginRedirect(returnTo);
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (isAuthEnabled) {
    return <ProtectedRouteWithRefresh>{children}</ProtectedRouteWithRefresh>;
  }
  return children;
};

const NavigateDashBoardAlreadyLogin = ({ children }) => {
  const { user } = useAuth();
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const AppLayout = ({ children }) => {
  const { user } = useAuth();
  const { isDark } = useTheme();
  const location = useLocation();
  const isAiAssistantRoute = location.pathname.includes('aiDebugger');
  const [apiDocsOpen, setApiDocsOpen] = React.useState(false);
  const [showScrollbar, setShowScrollbar] = React.useState(false);
  const scrollTimeoutRef = React.useRef(null);

  const handleContentScroll = React.useCallback(() => {
    setShowScrollbar(true);
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => setShowScrollbar(false), 1000);
  }, []);

  React.useEffect(() => () => {
    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
  }, []);

  if (!user) return children;

  return (
    <Layout style={{ minHeight: '100vh', display: 'flex' }}>
      <Sidebar />
      <Layout style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <Header onOpenApiDocs={() => setApiDocsOpen(true)} />
        <Content style={{
         background: isDark ? '#0d1117' : '#f5f7fa',
         overflowX: 'hidden',
         height: 'calc(100vh - 56px)',
         display: 'flex',
         flexDirection: 'column',
         overflow: 'hidden',
         minWidth: 0,
        }}>
          <div style={{
            background: isDark ? '#161b22' : '#fff',
            borderRadius: isAiAssistantRoute ? 0 : '8px',
            /* AI Assistant: edge-to-edge in main column (no side gutters / double frame) */
            padding: isAiAssistantRoute ? 0 : '24px',
            boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.5)' : '0 1px 3px rgba(0,0,0,0.1)',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            color: isDark ? '#e6edf3' : '#000',
            overflow: 'hidden',
          }}>
            <div
              className={`content-scroll ${showScrollbar ? 'content-scroll--visible' : ''}`}
              style={{
                flex: 1,
                minHeight: 0,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
              onScroll={handleContentScroll}
            >
              {children}
            </div>
          </div>
        </Content>
      </Layout>
      {apiDocsOpen && (
        <Suspense fallback={null}>
          <ApiDocsDrawerLazy
            open={apiDocsOpen}
            onClose={() => setApiDocsOpen(false)}
          />
        </Suspense>
      )}
    </Layout>
  );
};

function AppContent() {
  const { user } = useAuth();
  
  const userId = user?.username || user?.client_id || user?.email || 'guest';
  const API_BASE_URL = process.env.REACT_APP_AI_API_BASE_URL || 'https://assistant.clairai.cloud';
  
  const sessionId = React.useMemo(() => {
    const storedSessionId = sessionStorage.getItem('ai_chat_session_id');
    const storedUser = sessionStorage.getItem('ai_chat_user_for_session');
    
    if (storedSessionId && storedUser === userId) {
      return storedSessionId;
    }
    
    const now = new Date();
    const timestamp = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
    const shortId = Math.random().toString(36).substr(2, 6);
    const newSessionId = `chat-${userId}-${timestamp}-${shortId}`;
    
    sessionStorage.setItem('ai_chat_session_id', newSessionId);
    sessionStorage.setItem('ai_chat_user_for_session', userId);
    
    return newSessionId;
  }, [userId]);
  
  return (
    <AIProvider>
      <BackgroundRunner 
        API_BASE_URL={API_BASE_URL}
        sessionId={sessionId}
      />
      <AppLayout>
        <Routes>
          <Route path='/' element={ 
            user ? <Navigate to="/dashboard" replace /> : <LoginPage />
          } />
          <Route path="/login" element={ 
            <NavigateDashBoardAlreadyLogin>
              <LoginPage />
              </NavigateDashBoardAlreadyLogin>
            } />         
          <Route path="/callback" element={<CallbackPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
             {/* <Route
              path="/overview"
              element={
                <ProtectedRoute>
                  <OverviewPage/>
                </ProtectedRoute>
              }
            /> */}
            <Route
              path="/accounts"
              element={
                <ProtectedRoute>
                  <AccountsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pipeline-discovery"
              element={
                <ProtectedRoute>
                  <PipelineDiscoveryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/applications"
              element={
                <ProtectedRoute>
                  <ApplicationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/applications/:appSlug"
              element={
                <ProtectedRoute>
                  <ApplicationDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/application-discovery"
              element={<ApplicationDiscoveryRedirect />}
            />
            <Route
              path="/workflows"
              element={
                <ProtectedRoute>
                  <WorkflowsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workflow-analyzer"
              element={
                <ProtectedRoute>
                  <WorkflowsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/databricks-accounts"
              element={
                <ProtectedRoute>
                  <DatabricksAccountsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/metrics"
              element={
                <ProtectedRoute>
                  <MetricsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/databricks-metrics-onboarding"
              element={
                <ProtectedRoute>
                  <DatabricksMetricsOnboardingPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/logs"
              element={
                <ProtectedRoute>
                  <LogsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/alerts"
              element={
                <ProtectedRoute>
                  <AlertsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/incidents"
              element={
                <ProtectedRoute>
                  <IncidentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/integrations/connectors"
              element={
                <ProtectedRoute>
                  <ConnectorsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/costMonitor"
              element={
                <ProtectedRoute>
                  <CostMonitor />
                </ProtectedRoute>
              }
            />
            <Route
              path="/about"
              element={
                <ProtectedRoute>
                  <AboutPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/llmObservability"
              element={
                <ProtectedRoute>
                  <LLMObservability />
                </ProtectedRoute>
              }
            />
            <Route
              path="/aiDebugger"
              element={
                <ProtectedRoute>
                  <AIDebugger />
                </ProtectedRoute>
              }
            />
            
            <Route
              path="/active-services"
              element={
                <ProtectedRoute>
                  <ActiveServicesTracesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contact"
              element={
                <ProtectedRoute>
                  <ContactPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboards"
              element={
                <ProtectedRoute>
                  <DashboardList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/graphs-test"
              element={
                <ProtectedRoute>
                  <AllGraphsTest />
                </ProtectedRoute>
              }
            />
            {/* Catch-all route - redirect unknown paths to dashboard if authenticated, otherwise to login */}
            <Route
              path="*"
              element={
                user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />
              }
            />
          </Routes>
        </AppLayout>
      </AIProvider>
    );
  }

  function App() {
    return (
      <AuthProvider>
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <AppContent />
        </Router>
      </AuthProvider>
    );
  }
  
  export default App;
